import React, { useState, useEffect, useRef } from "react";
import { login } from "../../store/session";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import SessionConfirmationModal from "../SessionConfirmationModal";
import UserAgreementModal from "../UserAgreementModal";
import { FaLock, FaInfoCircle, FaSync } from "react-icons/fa";
import { refreshCSRFToken } from "../../utils/csrfFetch";

function LoginFormPage() {
  const dispatch = useDispatch();
  const sessionUser = useSelector((state) => state.session.user);
  const activeSession = useSelector((state) => state.session.activeSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [highlightAgreement, setHighlightAgreement] = useState(false);
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const agreementRef = useRef(null);
  const history = useHistory();

  // Check for saved email on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (sessionUser === null) {
      history.push('/login');
    }
  }, [sessionUser, history]);

  // Show confirmation modal when activeSession is set
  useEffect(() => {
    if (activeSession) {
      setShowConfirmation(true);
    } else {
      setShowConfirmation(false);
    }
  }, [activeSession]);

  // Reset highlight effect after a delay
  useEffect(() => {
    if (highlightAgreement) {
      const timer = setTimeout(() => {
        setHighlightAgreement(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightAgreement]);

  // Handle checkbox change
  const handleAgreementCheckboxChange = (e) => {
    setAgreedToTerms(e.target.checked);
    if (e.target.checked) {
      setHighlightAgreement(false);
    }
  };

  // Handle remember me checkbox change
  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  // Show full agreement modal
  const handleViewFullAgreement = (e) => {
    e.preventDefault();
    setShowAgreementModal(true);
  };

  // Close agreement modal
  const handleCloseAgreement = () => {
    setShowAgreementModal(false);
  };

  // Handle key press in password field
  const handlePasswordKeyPress = (e) => {
    if (e.key === 'Enter' && !agreedToTerms) {
      e.preventDefault();
      setHighlightAgreement(true);
      setErrors(['You must agree to the terms and conditions to log in']);

      // Scroll to and focus on the agreement checkbox
      if (agreementRef.current) {
        agreementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setIsSubmitting(true);
    setStatus(null);

    // Check if user has agreed to terms
    if (!agreedToTerms) {
      setHighlightAgreement(true);
      setErrors(['You must agree to the terms and conditions to log in']);
      setIsSubmitting(false);

      // Scroll to and focus on the agreement checkbox
      if (agreementRef.current) {
        agreementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Handle remember me
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    try {
      console.log("Submitting login form");
      const data = await dispatch(login(email, password, false, agreedToTerms));
      console.log("Login response in component:", data);

      if (data && data.hasActiveSession) {
        console.log("Active session detected in component");
        setIsSubmitting(false);
        return;
      }

      if (data && Array.isArray(data)) {
        console.log("Login errors:", data);

        // Check if the error is related to CSRF token
        if (data.some(err =>
          err.toLowerCase().includes('csrf') ||
          err.toLowerCase().includes('token') ||
          err.toLowerCase().includes('session expired')
        )) {
          setStatus('Refreshing session...');

          try {
            // Manually refresh the CSRF token
            await refreshCSRFToken();

            // Retry the login
            setStatus('Retrying login...');
            const retryData = await dispatch(login(email, password, false, agreedToTerms));

            // Handle the retry response
            if (retryData && retryData.hasActiveSession) {
              setIsSubmitting(false);
              setStatus(null);
              return;
            }

            if (retryData && Array.isArray(retryData)) {
              setErrors(retryData);
              setIsSubmitting(false);
              setStatus(null);
              return;
            }

            // If we get here, login was successful after retry
            handleSuccessfulLogin(retryData);
          } catch (retryError) {
            console.error("Error during token refresh and retry:", retryError);
            setErrors(['Session expired. Please refresh the page and try again.']);
            setIsSubmitting(false);
            setStatus(null);
          }
          return;
        }

        // Regular errors
        setErrors(data);
        setIsSubmitting(false);
        return;
      }

      // If we get here, login was successful
      handleSuccessfulLogin(data);
    } catch (error) {
      console.error("Login submission error:", error);
      setErrors(['An unexpected error occurred. Please try again.']);
      setIsSubmitting(false);
    }
  };

  // Helper function to handle successful login
  const handleSuccessfulLogin = (data) => {
    setIsSubmitting(false);
    setStatus(null);

    if (data && !data.is_admin) {
      // Redirect to home for non-admin users
      history.push('/');
    } else if (data) {
      // Admin user, redirect to admin dashboard
      history.push('/admin/dashboard');
    }
  };

  // Handle confirmation to force login
  const handleForceLogin = async () => {
    console.log("Force login attempt");
    setIsSubmitting(true);
    setStatus(null);
    setErrors([]);

    try {
      // Clear any existing session data first
      localStorage.removeItem('sessionId');

      // Check if user has agreed to terms
      if (!agreedToTerms) {
        setHighlightAgreement(true);
        setErrors(['You must agree to the terms and conditions to log in']);
        setShowConfirmation(false);
        setIsSubmitting(false);
        return;
      }

      // Handle remember me
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      const data = await dispatch(login(email, password, true, agreedToTerms));
      console.log("Force login response:", data);

      if (data && Array.isArray(data)) {
        // Check if the error is related to CSRF token
        if (data.some(err =>
          err.toLowerCase().includes('csrf') ||
          err.toLowerCase().includes('token') ||
          err.toLowerCase().includes('session expired')
        )) {
          setStatus('Refreshing session...');

          try {
            // Manually refresh the CSRF token
            await refreshCSRFToken();

            // Retry the login
            setStatus('Retrying login...');
            const retryData = await dispatch(login(email, password, true, agreedToTerms));

            // Handle the retry response
            if (retryData && Array.isArray(retryData)) {
              setErrors(retryData);
              setShowConfirmation(false);
              setIsSubmitting(false);
              setStatus(null);
              return;
            }

            if (retryData && retryData.hasActiveSession) {
              // If we still get an active session response, something went wrong
              setErrors(['Failed to log out other session. Please try again.']);
              setShowConfirmation(false);
              setIsSubmitting(false);
              setStatus(null);
              return;
            }

            // If we get here, login was successful after retry
            handleSuccessfulForceLogin(retryData);
          } catch (retryError) {
            console.error("Error during token refresh and retry:", retryError);
            setErrors(['Session expired. Please refresh the page and try again.']);
            setShowConfirmation(false);
            setIsSubmitting(false);
            setStatus(null);
          }
          return;
        }

        // Regular errors
        setErrors(data);
        setShowConfirmation(false);
        setIsSubmitting(false);
      } else if (data && data.hasActiveSession) {
        // If we still get an active session response, something went wrong
        setErrors(['Failed to log out other session. Please try again.']);
        setShowConfirmation(false);
        setIsSubmitting(false);
      } else {
        // Successful login
        handleSuccessfulForceLogin(data);
      }
    } catch (error) {
      console.error("Force login error:", error);
      setErrors(['An unexpected error occurred. Please try again.']);
      setShowConfirmation(false);
      setIsSubmitting(false);
    }
  };

  // Helper function to handle successful force login
  const handleSuccessfulForceLogin = (data) => {
    setShowConfirmation(false);
    setIsSubmitting(false);
    setStatus(null);

    if (data && !data.is_admin) {
      // Successful login for non-admin user, redirect to home
      console.log("Non-admin user force logged in, redirecting to home");
      history.push('/');
    } else {
      // Admin user, redirect to admin dashboard
      history.push('/admin/dashboard');
    }
  };

  // Handle cancellation of force login
  const handleCancelLogin = () => {
    setShowConfirmation(false);
    // Clear the active session from the store
    dispatch({ type: 'session/CLEAR_ACTIVE_SESSION' });
  };

  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-gray-100 p-5 text-xl">
      {/* <img
          className="size-28  mix-blend-multiply absolute left-12 top-12"
          src='/images/modern-tech-logo2.png'
          alt='company-logo'
          /> */}
      {showConfirmation && activeSession && (
        <SessionConfirmationModal
          sessionInfo={activeSession}
          onConfirm={handleForceLogin}
          onCancel={handleCancelLogin}
        />
      )}
      {showAgreementModal && (
        <UserAgreementModal
          viewOnly={true}
          onClose={handleCloseAgreement}
        />
      )}
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-lg">
        <div className="flex items-center">
          <img
            className="mix-blend-multiply"
            src='/images/yardiq-logo.png'
            alt='company-logo'
            />
          </div>
        <h1 className="text-blue-600 w-36 text-5xl my-8 font-semibold">
          Log In</h1>

        {/* Status message for token refresh */}
        {status && (
          <div className="mb-4 p-3 bg-blue-100 text-blue-800 rounded-md flex items-center justify-center">
            <FaSync className="animate-spin mr-2" />
            <span>{status}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full">
          <ul className="flex flex-col items-center list-none p-0 mb-6 text-red-500 text-lg">
            {errors.map((error, idx) => (
              <li key={idx} className="mb-2">{error}</li>
            ))}
          </ul>
          <div className="mb-6">
            <label htmlFor="email" className="block mb-2 text-gray-700 text-xl">
              Email
            </label>
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full p-4 border border-gray-300 bg-blue-100 rounded-md text-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div className="mb-6">
            <label htmlFor="password" className="block mb-2 text-gray-700 text-xl">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handlePasswordKeyPress}
              required
              className="w-full p-4 border border-gray-300 bg-blue-100 rounded-md text-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </div>

          {/* Remember me checkbox */}
          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={handleRememberMeChange}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="rememberMe" className="ml-2 block text-gray-700 text-lg">
              Remember my email
            </label>
          </div>

          {/* Agreement checkbox with improved messaging */}
          <div
            ref={agreementRef}
            className={`mb-6 p-4 border rounded-lg transition-all duration-300 ${
              highlightAgreement
                ? 'border-red-500 bg-red-50 shadow-md animate-pulse'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="mb-2 text-gray-600 text-sm">
              For legal and compliance reasons, you must acknowledge our terms of service each time you log in.
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="agreement"
                checked={agreedToTerms}
                onChange={handleAgreementCheckboxChange}
                className={`h-6 w-6 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200 ${
                  highlightAgreement ? 'ring-2 ring-red-500' : ''
                }`}
              />
              <label htmlFor="agreement" className="ml-2 block text-gray-700 text-lg">
                I agree to the <button
                  type="button"
                  onClick={handleViewFullAgreement}
                  className="text-blue-600 underline focus:outline-none font-medium"
                >
                  terms and conditions
                </button>
              </label>
            </div>
          </div>

          {/* Login button with tooltip */}
          <div className="relative">
            <button
              type="submit"
              className={`w-full my-4 p-4 rounded-md text-2xl font-bold cursor-pointer transition-colors duration-300 ${
                agreedToTerms && !isSubmitting
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-400 text-gray-100 cursor-not-allowed'
              }`}
              disabled={!agreedToTerms || isSubmitting}
              onMouseEnter={() => !agreedToTerms && setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {isSubmitting ? (
                <>
                  <FaSync className="inline-block mr-4 mb-1 animate-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <FaLock className="inline-block mr-4 mb-1" />
                  Log In
                </>
              )}
            </button>

            {/* Tooltip */}
            {showTooltip && !agreedToTerms && (
              <div className="absolute left-1/2 transform -translate-x-1/2 -top-16 bg-gray-800 text-white text-sm px-4 py-2 rounded shadow-lg w-64 z-10">
                <div className="flex items-center">
                  <FaInfoCircle className="mr-2 text-yellow-300" />
                  <span>Please check the agreement box to enable login</span>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-gray-800"></div>
              </div>
            )}
          </div>

          <div className="text-sm text-gray-500 mt-10">
            By clicking on the Log In button, you agree to the
            <div className="flex flex-row">
              <p className="text-blue-600 hover:underline"> Terms of Service </p>
              <p className="text-gray-500 mx-2">and</p>
              <p className="text-blue-600 hover:underline"> Privacy Policy</p>.
            </div>
          </div>
        </form>
      </div>
        <div className="flex flex-col justify-between items-center mt-10">
          <img src={'/images/modern-tech-logo2.png'} alt="Modern Technology Logo" className="size-30" />
          <div className="text-sm text-gray-500 mt-4">
          ©️Modern Technology LLC 2025
          </div>
        </div>
    </div>
  );
}

export default LoginFormPage;
